import { HttpClient, HttpEvent, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpMethod, HypermediaLink } from '@cemex-core/cmx-api';
import { OAuth2 } from '@cemex-core/cmx-api/models/auth';
import { Store } from '@ngrx/store';
import { getAuth, getLanguage } from '../store/selectors';
import { AuthState } from '../typings/auth';
import { State } from '../typings/state';
import { ConfigService } from './config.service';

export interface CustomHeaders {
  [name: string]: string | string[];
}

export enum ResponseContentType {
  ArrayBuffer = 'arraybuffer',
  Blob = 'blob',
  JSON = 'json',
  Text = 'text',
}

export class CmxHttpOptions {
  headers?: CustomHeaders;
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  // tslint:disable-next-line:no-any
  responseType?: any = ResponseContentType.JSON;
}

@Injectable()
export class CmxHttpClient {
  private apiHost: string;
  private appCode: string;

  private activeLanguage = '';
  private auth: AuthState;

  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService,
    private store: Store<State>,
  ) {
    const config = this.configService.getConfig();
    this.apiHost = config.apiHost ? config.apiHost : '';
    this.appCode = 'DCMWebTool_App';
    this.store.select(getLanguage).subscribe((lang) => (this.activeLanguage = lang));
    this.store.select(getAuth).subscribe((auth) => (this.auth = auth));
  }

  /**
   * To prevent URLs being constructed with a double-slash at the start of the local path,
   * remove the first character of apiPath when joining it and this.apiUrl would create
   * such a malformed URL.
   * @param apiPath an endpoint path within the API
   */
  public cleanUrl(apiPath: string): string {
    if (this.apiHost.substr(-1) === '/' && apiPath.substr(0, 1) === '/') {
      // BOTH have a slash - drop one to avoid outputting a malformed URL.
      apiPath = apiPath.substr(1);
    } else if (this.apiHost.substr(-1) !== '/' && apiPath.substr(0, 1) !== '/') {
      // NEITHER one has a slash.  Oops!
      apiPath = `/${apiPath}`;
    }
    // Otherwise, things should be fine.
    return `${this.apiHost}${apiPath}`;
  }

  public get<T>(apiPath: string, options?: CmxHttpOptions) {
    const httpOptions = this.createOptions(options);
    return this.httpClient.get<T>(this.cleanUrl(apiPath), {
      headers: this.getHeaders(httpOptions.headers),
      params: httpOptions.params,
      responseType: httpOptions.responseType,
    });
  }

  // tslint:disable-next-line:no-any
  public post<T>(apiPath: string, body: any | null, options?: CmxHttpOptions) {
    const httpOptions = this.createOptions(options);
    return this.httpClient.post<T>(this.cleanUrl(apiPath), body, {
      headers: this.getHeaders(httpOptions.headers),
      params: httpOptions.params,
      responseType: httpOptions.responseType,
    });
  }

  // tslint:disable-next-line:no-any
  public patch<T>(apiPath: string, body: any | null, options?: CmxHttpOptions) {
    const httpOptions = this.createOptions(options);
    return this.httpClient.patch<T>(this.cleanUrl(apiPath), body, {
      headers: this.getHeaders(httpOptions.headers),
      params: httpOptions.params,
      responseType: httpOptions.responseType,
    });
  }

  // tslint:disable-next-line:no-any
  public put<T>(apiPath: string, body: any | null, options?: CmxHttpOptions) {
    const httpOptions = this.createOptions(options);
    return this.httpClient.put<T>(this.cleanUrl(apiPath), body, {
      headers: this.getHeaders(httpOptions.headers),
      params: httpOptions.params,
      responseType: httpOptions.responseType,
    });
  }

  public delete<T>(apiPath: string, options?: CmxHttpOptions) {
    const httpOptions = this.createOptions(options);
    return this.httpClient.delete<T>(this.cleanUrl(apiPath), {
      headers: this.getHeaders(httpOptions.headers),
      params: httpOptions.params,
      responseType: httpOptions.responseType,
    });
  }

  // tslint:disable-next-line:no-any
  public upload<T>(apiPath: string, file: any | null, options?: CmxHttpOptions) {
    const httpOptions = this.createOptions(options);
    return this.httpClient.request<HttpEvent<T>>('POST', this.cleanUrl(apiPath), {
      body: file,
      headers: this.getHeaders(httpOptions.headers),
      params: httpOptions.params,
      responseType: httpOptions.responseType,
      observe: 'events',
      reportProgress: true,
    });
  }

  // tslint:disable-next-line:no-any
  public fetchLink<T>(link: HypermediaLink, body?: any, _options?: CmxHttpOptions) {
    switch (link.method) {
      case HttpMethod.GET:
        return this.get<T>(link.href, _options);
      case HttpMethod.POST:
        return this.post<T>(link.href, body, _options);
      case HttpMethod.PATCH:
        return this.patch<T>(link.href, body, _options);
      case HttpMethod.DELETE:
        return this.delete<T>(link.href, _options);
      case HttpMethod.PUT:
        return this.put<T>(link.href, body, _options);
    }
  }

  private createOptions(options?: CmxHttpOptions): CmxHttpOptions {
    let httpOptions: CmxHttpOptions;
    if (options) {
      httpOptions = {
        headers: options.headers ? options.headers : {},
        params: options.params ? options.params : {},
        responseType: options.responseType ? options.responseType : ResponseContentType.JSON,
      };
    } else {
      httpOptions = {
        headers: {},
        params: {},
        responseType: ResponseContentType.JSON,
      };
    }
    return httpOptions;
  }

  private getHeaders(customHeaders?: CustomHeaders): HttpHeaders {
    const oauth2: OAuth2 = this.auth.oauth2
      ? this.auth.oauth2
      : {
          access_token: '',
          expires_in: 0,
          refresh_token: '',
          scope: '',
        };
    const jwt: string = this.auth.jwt ? this.auth.jwt : '';
    const language = this.activeLanguage;
    return new HttpHeaders({
      authorization: `Bearer ${oauth2.access_token}`,
      jwt,
      'app-code': this.appCode,
      'accept-language': language ? language : 'en_US',
      ...customHeaders,
    });
  }
}
